import React, { useCallback } from 'react'
import { ContentAdd } from '@fs/zion-icon'
import {
  Alert,
  LayoutBand,
  Button,
  colors,
  H5,
  ListItem,
  MenuOverlay,
  Paragraph,
  Separator,
  useOverlay,
} from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import AddExistingPersonFlow from '../../panels/addExistingPersonPanel/AddExistingPersonFlow'
import { ADD_EXISTING_PERSON_ENTRY_POINTS } from '../../panels/addExistingPersonPanel/utils/addExistingPersonConstants'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import useNewGroupSave from '../hooks/useNewGroupSave'
import useAddNamesEntryPoint, { ADD_NAMES_ENTRY_POINTS } from '../../../../hooks/useAddNamesEntryPoint'

/**
 * The card at the end of the names list that starts a group.
 *
 * The Add Existing Person sheet is a sibling of the menu rather than something the menu renders: it
 * has to stay up after the menu item that opened it is gone, and again behind each confirmation step.
 *
 * @component
 * @returns {JSX.Element}
 */
const NewGroupCard = () => {
  const [t] = useTranslation()
  const { isReadOnlyCollection } = useCollectionContextProvider()
  const { createGroup, error, clearError } = useNewGroupSave()
  // No record, so the new name starts a group rather than joining the last one touched.
  const { open: handleAddNewPerson } = useAddNamesEntryPoint(ADD_NAMES_ENTRY_POINTS.NEW_GROUP)

  const addExistingPersonOverlay = useOverlay()

  // Cleared on the way in rather than on the way out, so a failure stays readable until the patron
  // goes back for another attempt.
  const handleAddExistingPerson = useCallback(
    (event) => {
      clearError()
      addExistingPersonOverlay.handleClick(event)
    },
    [clearError, addExistingPersonOverlay]
  )

  const buttonLabel = t('new-group-card.button', 'Add person to new group')

  return (
    <>
      <LayoutBand color={colors.blue02} marginY="xxs">
        <H5>{t('new-group-card.title', 'New group')}</H5>
        <Separator size="nano" />
        <Paragraph size="sm">
          {t(
            'new-group-card.description',
            'Move people from any group on this record into a new group, or add someone not listed yet.'
          )}
        </Paragraph>
        <Separator size="nano" />
        <Paragraph size="sm">
          {t(
            'new-group-card.existing-group-hint',
            'To add to an existing group, open a person in it and edit Relationships.'
          )}
        </Paragraph>
        <Separator size="xxs" />
        <MenuOverlay
          menuButton={
            <Button
              emphasis="medium"
              Icon={ContentAdd}
              linkName="Add Person To New Group"
              data-testid="add-person-to-new-group-button"
              disabled={isReadOnlyCollection}
              fullWidth
            >
              {buttonLabel}
            </Button>
          }
          aria-label={buttonLabel}
        >
          <ListItem
            primaryText={t('relationships-card.add-existing-person', 'Add existing person')}
            onClick={handleAddExistingPerson}
            data-testid="new-group-add-existing-person"
          />
          <ListItem
            primaryText={t('relationships-card.add-new-person', 'Add new person')}
            onClick={() => handleAddNewPerson()}
            data-testid="new-group-add-new-person"
          />
        </MenuOverlay>
        {error && (
          <>
            <Separator size="nano" />
            <Alert type="error">
              {t('new-group-card.save-error', 'Error creating the group. Please try again later.')}
            </Alert>
          </>
        )}
      </LayoutBand>
      <Separator size="xxs" />
      <AddExistingPersonFlow
        overlay={addExistingPersonOverlay}
        entryPoint={ADD_EXISTING_PERSON_ENTRY_POINTS.NEW_GROUP}
        onSave={createGroup}
      />
    </>
  )
}

export default NewGroupCard
